<template>
  <div class="absolute top-4 right-4 z-10 bg-white rounded-md">
    <UButton
      v-if="showFavoriteButton"
      :aria-label="t('a11y.favorite-button')"
      v-bind="
        isFavorite
          ? {
              variant: 'solid',
              color: 'error',
              icon: 'i-heroicons-heart-20-solid',
            }
          : { variant: 'subtle', color: 'neutral', icon: 'i-heroicons-heart' }
      "
      size="md"
      class="absolute top-4 right-4 z-10"
      @click="handleFavoriteButton()"
    />
  </div>

  <!-- Modal de autenticación -->
  <AuthModal
    v-model="isAuthModalOpen"
    :modaltitle="t('outings.favorite_modal.auth_title')"
  >
    <template #default>
      <div class="p-6">
        <p class="text-gray-600 mb-4">
          {{ t('outings.favorite_modal.auth_description') }}
        </p>
      </div>
    </template>
  </AuthModal>

  <!-- Modal de selección: Favoritos o Escapadas -->
  <AuthModal
    v-model="isSelectionModalOpen"
    :modaltitle="t('outings.favorite_modal.selection_title')"
    :modaldescription="t('outings.favorite_modal.selection_description')"
  >
    <template #default>
      <CottageFavoriteSelection
        @select-favorites="selectFavorites"
        @select-outings="selectOutings"
      />
    </template>
  </AuthModal>

  <!-- Modal de favoritos -->
  <AuthModal
    v-model="isFavoritesModalOpen"
    :modaltitle="t('views.cottage-bookmarks.title')"
  >
    <template #default>
      <CottageBookmarks :item="item" @submit-success="closeModal" />
    </template>
  </AuthModal>

  <!-- Modal de escapadas -->
  <AuthModal
    v-model="isOutingsModalOpen"
    :modaltitle="t('outings.favorite_modal.add_modal_title')"
  >
    <template #default>
      <CottageOutings :item="item" @submit-success="closeModal" />
    </template>
  </AuthModal>
</template>

<script setup lang="ts">
import { ref, watchEffect, onMounted, onUnmounted } from 'vue'
import { browserFetch as fetchBookmarkDelete } from '~~/modules/api/rurality/v3/bookmarks/delete'
import { useUserStore } from '~~/modules/auth/stores/userStore'
import type { CottagePage } from '../entities/CottagePage'
import { sendConnectifSaveFavoriteEvent } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

const userStore = useUserStore()

const showFavoriteButton = ref<boolean>(false)
const isFavorite = ref<boolean>(false)
const wasWaitingForAuth = ref<boolean>(false)

type itemType = Pick<
  CottagePage,
  'media' | 'name' | 'village' | 'province' | 'id'
>

interface propsInterface {
  item: itemType
}

const props = defineProps<propsInterface>()

// Estados
type States = 'IDLE' | 'AUTH_REQUIRED' | 'SELECTION' | 'FAVORITES' | 'OUTINGS'

const currentState = ref<States>('IDLE')

// Computed para controlar los modales
const isAuthModalOpen = computed({
  get: () => currentState.value === 'AUTH_REQUIRED',
  set: value => {
    if (!value) currentState.value = 'IDLE'
  },
})

const isSelectionModalOpen = computed({
  get: () => currentState.value === 'SELECTION',
  set: value => {
    if (!value) currentState.value = 'IDLE'
  },
})

const isFavoritesModalOpen = computed({
  get: () => currentState.value === 'FAVORITES',
  set: value => {
    if (!value) currentState.value = 'IDLE'
  },
})

const isOutingsModalOpen = computed({
  get: () => currentState.value === 'OUTINGS',
  set: value => {
    if (!value) currentState.value = 'IDLE'
  },
})

const handleFavoriteButton = async () => {
  if (isFavorite.value) {
    const data = await fetchBookmarkDelete({
      cottageUid: props.item.id,
    })
    if (data.success) {
      isFavorite.value = false
      currentState.value = 'IDLE'
    }
  } else {
    // Check if user is logged in
    if (!userStore.getLogged.value) {
      wasWaitingForAuth.value = true
      currentState.value = 'AUTH_REQUIRED'
    } else {
      currentState.value = 'SELECTION'
    }
    sendConnectifSaveFavoriteEvent()
  }
}

const selectFavorites = () => {
  currentState.value = 'FAVORITES'
}

const selectOutings = () => {
  currentState.value = 'OUTINGS'
}

const closeModal = () => {
  currentState.value = 'IDLE'
}

const init = async () => {
  const user = userStore.getUserdata.value
  if (!user.logged) {
    showFavoriteButton.value = true
  } else if (user.logged && user.params?.cottage_bookmarks) {
    showFavoriteButton.value = true
    const cottageBookmarks = user.params?.cottage_bookmarks || []
    isFavorite.value = cottageBookmarks.some(
      (bookmark: { cottageId: string }) => bookmark.cottageId === props.item.id,
    )
  }
}

const unsubscribe = watch(
  () => userStore.getLogged.value,
  () => {
    init()

    // Si estábamos esperando auth y ahora el usuario está logueado, abrir selección
    if (wasWaitingForAuth.value && userStore.getLogged.value) {
      wasWaitingForAuth.value = false
      currentState.value = 'SELECTION'
    }
  },
)

onUnmounted(() => {
  unsubscribe()
})

watchEffect(() => {
  const cottageBookmarks =
    userStore.getUserdata.value.params?.cottage_bookmarks || []
  isFavorite.value = cottageBookmarks.some(
    (bookmark: { cottageId: string }) => bookmark.cottageId === props.item.id,
  )
})

onMounted(() => {
  init()
})
</script>
